/**
 * Vincent #98 (2026-05-12) — Gestion des rôles (display-only).
 *
 * Permet à un admin de renommer les libellés des rôles internes pour son org.
 * Les rôles techniques (admin/concepteur/employee/etc) restent inchangés en DB
 * — c'est UNIQUEMENT l'affichage qui change. Aucun impact sur les permissions.
 *
 * Exemple Finance Libre HQ : "Admin" → "Admin plateforme", "Concepteur" →
 * "Concepteur de formations", "Employé avancé" → "Directeur / agent expérimenté",
 * "Employé formé" → "Conseiller", "Nouvel employé" → "Stagiaire".
 */
import { useCallback, useEffect, useState } from "react";
import { cleTechniqueAffichee } from "../lib/canonicalRoles";
import axios from "axios";
import { toast } from "../components/ui/sonner";
import { useLang } from "../contexts/LanguageContext";

import { API_BASE as API } from "../lib/apiBase";

// Vincent #123 (Eric 2026-05-13) — Localized display names for canonical roles.
// Les `defaults` renvoyés par le backend sont en français figé ; on veut afficher
// un libellé adapté à la langue UI dans le tableau de Gestion des rôles.
const ROLE_DISPLAY = {
  fr: {
    founder: "Fondateur",
    admin: "Admin",
    concepteur: "Concepteur",
    employee_advanced: "Employé avancé",
    employee_trained: "Employé formé",
    employee_new: "Nouvel employé",
    employee: "Employé",
    viewer: "Visiteur",
  },
  en: {
    founder: "Founder",
    admin: "Admin",
    concepteur: "Designer",
    employee_advanced: "Advanced employee",
    employee_trained: "Trained employee",
    employee_new: "New employee",
    employee: "Employee",
    viewer: "Viewer",
  },
};

export default function RoleLabelsPage() {
  const { lang } = useLang();
  const [defaults, setDefaults] = useState({});
  const [labels, setLabels] = useState({});
  // Tâche 1387 (Eric 2026-06-30) — Colonne anglaise. Les libellés français
  // saisis par l'organisation sont des DONNÉES : les traduire tout seul
  // reviendrait à inventer son vocabulaire. Elle saisit donc ses noms anglais.
  const [defaultsEn, setDefaultsEn] = useState({});
  const [labelsEn, setLabelsEn] = useState({});
  const [sansAnglais, setSansAnglais] = useState([]);
  const [canonicalRoles, setCanonicalRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/organizations/role-labels`, { withCredentials: true });
      setDefaults(data.defaults || {});
      setLabels(data.role_labels || {});
      setDefaultsEn(data.defaults_en || {});
      setLabelsEn(data.role_labels_en || {});
      setSansAnglais(data.roles_sans_anglais || []);
      setCanonicalRoles(data.canonical_roles || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || (lang === "fr" ? "Impossible de charger" : "Failed to load"));
    } finally {
      setLoading(false);
    }
  }, [lang]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      // On envoie UNIQUEMENT les rôles dont le libellé custom diffère du défaut
      const customOnly = {};
      const customOnlyEn = {};
      for (const role of canonicalRoles) {
        const current = (labels[role] || "").trim();
        if (current && current !== (defaults[role] || "")) customOnly[role] = current;
        const currentEn = (labelsEn[role] || "").trim();
        if (currentEn && currentEn !== (defaultsEn[role] || "")) customOnlyEn[role] = currentEn;
      }
      const { data } = await axios.put(
        `${API}/api/organizations/role-labels`,
        { role_labels: customOnly, role_labels_en: customOnlyEn },
        { withCredentials: true },
      );
      toast.success(lang === "fr"
        ? `Sauvegardé · ${data.custom_count} libellé(s) en français, ${data.custom_count_en} en anglais`
        : `Saved · ${data.custom_count} French label(s), ${data.custom_count_en} English`);
      setLabels(data.role_labels || {});
      setLabelsEn(data.role_labels_en || {});
      setSansAnglais(canonicalRoles.filter((r) => customOnly[r] && !customOnlyEn[r]));
    } catch (e) {
      toast.error(e.response?.data?.detail || (lang === "fr" ? "Échec de la sauvegarde" : "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const resetRole = (role) => {
    setLabels((p) => ({ ...p, [role]: defaults[role] || "" }));
    setLabelsEn((p) => ({ ...p, [role]: defaultsEn[role] || "" }));
  };

  if (loading) {
    return <div className="p-6 text-zinc-500">{lang === "fr" ? "Chargement..." : "Loading..."}</div>;
  }

  return (
    <div className="p-4 lg:p-6 max-w-3xl" data-testid="role-labels-page">
      <div className="mt-4 mb-6">
        <h1 className="text-2xl lg:text-3xl font-bold text-zinc-900">
          👥 {lang === "fr" ? "Gestion des rôles" : "Role management"}
        </h1>
        <p className="text-sm text-zinc-600 mt-2 max-w-2xl">
          {lang === "fr"
            ? "Renommez les libellés des rôles affichés dans l'application pour votre organisation. Les permissions techniques restent inchangées — c'est uniquement l'affichage qui s'adapte à votre vocabulaire interne."
            : "Rename the role labels displayed across the app for your organisation. Technical permissions stay unchanged — only the display adapts to your internal vocabulary."}
        </p>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-5 text-xs text-amber-900">
        💡 <strong>{lang === "fr" ? "Exemple Finance Libre HQ" : "Finance Libre HQ example"}</strong> :
        {lang === "fr" ? (
          <>
            {" "}Fondateur → Fondateur · Admin → <em>Admin plateforme</em> · Concepteur → <em>Concepteur de formations</em> ·
            Employé avancé → <em>Directeur / agent expérimenté</em> · Employé formé → <em>Conseiller</em> ·
            Nouvel employé → <em>Stagiaire</em>.
          </>
        ) : (
          <>
            {" "}Founder → Founder · Admin → <em>Platform admin</em> · Designer → <em>Training designer</em> ·
            Advanced employee → <em>Director / experienced agent</em> · Trained employee → <em>Advisor</em> ·
            New employee → <em>Trainee</em>.
          </>
        )}
      </div>

      {sansAnglais.length > 0 && (
        <div
          className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 mb-4 text-xs text-blue-900"
          data-testid="role-labels-manque-anglais"
        >
          {/* Tâche 1387 — On DÉSIGNE le trou au lieu de le combler par une
              traduction inventée : c'est ce trou qui produisait
              « Conseiller actif » au milieu d'une page anglaise. */}
          <strong>
            {lang === "fr"
              ? `${sansAnglais.length} rôle(s) renommé(s) en français n'ont pas encore de nom anglais`
              : `${sansAnglais.length} role(s) renamed in French have no English name yet`}
          </strong>{" "}
          {lang === "fr"
            ? "— ils s'affichent donc en français dans la version anglaise. Nous ne traduisons pas votre vocabulaire à votre place :"
            : "— they therefore show up in French in the English version. We do not translate your vocabulary for you:"}{" "}
          {sansAnglais.map((r) => labels[r]).filter(Boolean).join(" · ")}
        </div>
      )}

      <div className="hidden sm:flex items-center gap-3 px-3 mb-1 text-[11px] font-semibold uppercase tracking-wider text-zinc-400">
        <span className="flex-shrink-0 w-44">{lang === "fr" ? "Rôle" : "Role"}</span>
        <span className="flex-1 min-w-[160px]">{lang === "fr" ? "Nom en français" : "French name"}</span>
        <span className="flex-1 min-w-[160px]">{lang === "fr" ? "Nom en anglais" : "English name"}</span>
      </div>

      <div className="space-y-2 mb-5">
        {canonicalRoles.map((role) => {
          const displayName = (ROLE_DISPLAY[lang] && ROLE_DISPLAY[lang][role]) || defaults[role] || role;
          const modifie = labels[role] !== defaults[role] || labelsEn[role] !== defaultsEn[role];
          return (
          <div key={role} className="bg-white border border-zinc-200 rounded-lg p-3 flex flex-wrap items-center gap-3" data-testid={`role-row-${role}`}>
            <div className="flex-shrink-0 w-44">
              <div className="text-sm font-semibold text-zinc-900">{displayName}</div>
              <div className="text-[11px] text-zinc-400 mt-0.5">
                <span className="text-zinc-400">{lang === "fr" ? "Clé technique" : "Technical key"} :</span>{" "}
                {/* Tâche 1402 — La clé MONTRÉE est `designer` ; la clé qui fait
                    autorité reste `concepteur` en base (voir la note dans
                    `lib/canonicalRoles.js`). */}
                <code className="text-[10px] text-zinc-500 bg-zinc-100 px-1 py-0.5 rounded" data-testid={`role-cle-technique-${role}`}>{cleTechniqueAffichee(role)}</code>
              </div>
            </div>
            <input
              type="text"
              value={labels[role] || ""}
              onChange={(e) => setLabels((p) => ({ ...p, [role]: e.target.value }))}
              maxLength={100}
              placeholder={defaults[role]}
              aria-label={`${displayName} — ${lang === "fr" ? "nom en français" : "French name"}`}
              className="flex-1 min-w-[160px] px-3 py-2 border border-zinc-300 rounded-lg text-sm"
              data-testid={`role-input-${role}`}
            />
            <input
              type="text"
              value={labelsEn[role] || ""}
              onChange={(e) => setLabelsEn((p) => ({ ...p, [role]: e.target.value }))}
              maxLength={100}
              placeholder={defaultsEn[role]}
              aria-label={`${displayName} — ${lang === "fr" ? "nom en anglais" : "English name"}`}
              className="flex-1 min-w-[160px] px-3 py-2 border border-zinc-300 rounded-lg text-sm"
              data-testid={`role-input-en-${role}`}
            />
            {modifie && (
              <button
                onClick={() => resetRole(role)}
                className="text-xs text-zinc-500 hover:text-zinc-900 underline"
                data-testid={`role-reset-${role}`}
              >
                {lang === "fr" ? "Rétablir" : "Reset"}
              </button>
            )}
          </div>
          );
        })}
      </div>

      <div className="flex items-center gap-3">
        <button
          onClick={save}
          disabled={saving}
          className="px-5 py-2.5 bg-[#002FA7] hover:bg-[#001F7A] text-white text-sm font-semibold rounded-lg disabled:opacity-50"
          data-testid="role-labels-save-btn"
        >
          {saving ? (lang === "fr" ? "Sauvegarde..." : "Saving...") : (lang === "fr" ? "Sauvegarder" : "Save")}
        </button>
        <p className="text-xs text-zinc-500">
          ⚠️ {lang === "fr"
            ? "Les permissions techniques (admin, fondateur, etc.) ne sont pas affectées — uniquement l'affichage."
            : "Technical permissions are NOT affected — only the display."}
        </p>
      </div>
    </div>
  );
}
